import { HttpErrorResponse } from '@angular/common/http';
import { Observable, firstValueFrom, from, map, of, switchMap, tap } from 'rxjs';
import { Overhead, ProcessSheets, TId } from 'src/app/models';
import { ICRUDResource, ProcessSheetsResource } from 'src/app/resources';
import { MasterApiService } from 'src/app/services/master-api.service';

/** Manages process sheet ↔ overhead links (not Overhead CRUD). */
export class ProcessSheetOverheadsResource implements ICRUDResource<Overhead> {
  key = 'id' as const;
  resourceName = 'processSheetOverhead';

  private overheadIds: TId[] = [];
  private linkedOverheads: Overhead[] = [];

  constructor(
    private api: MasterApiService,
    private processSheetId: TId,
  ) {}

  private get processSheetsResource(): ProcessSheetsResource {
    return this.api.resources.product.processSheets as unknown as ProcessSheetsResource;
  }

  async reload(): Promise<void> {
    const processSheet = await firstValueFrom(
      this.processSheetsResource.getProcessSheet(this.processSheetId),
    );
    this.overheadIds = [...(processSheet.overheadIds ?? [])];
    if (this.overheadIds.length === 0) {
      this.linkedOverheads = [];
      return;
    }

    this.linkedOverheads = (
      await Promise.all(
        this.overheadIds.map((id) => firstValueFrom(this.api.resources.overheads.get(id))),
      )
    ).filter((o): o is Overhead => Boolean(o?.id));
  }

  get linkedIds(): TId[] {
    return [...this.overheadIds];
  }

  getAll(): Observable<Overhead[]> {
    return of([...this.linkedOverheads]);
  }

  list(): Observable<{ total: number; data: Overhead[] }> {
    const data = [...this.linkedOverheads];
    return of({ total: data.length, data });
  }

  get(id: TId): Observable<Overhead> {
    const found = this.linkedOverheads.find((o) => o.id === id);
    if (!found) {
      throw new HttpErrorResponse({ status: 404, statusText: 'Not found' });
    }
    return of(found);
  }

  post(entity: Partial<Overhead> & { overheadId?: TId }): Observable<Overhead> {
    const overheadId = (entity.overheadId ?? entity.id) as TId | undefined;
    if (!overheadId) {
      throw new Error('overheadId is required');
    }
    if (this.overheadIds.includes(overheadId)) {
      return from(this.reload()).pipe(
        switchMap(() => {
          const existing = this.linkedOverheads.find((o) => o.id === overheadId);
          if (!existing) {
            throw new HttpErrorResponse({ status: 404, statusText: 'Not found' });
          }
          return of(existing);
        }),
      );
    }

    const overheadIds = [...this.overheadIds, overheadId];
    return this.processSheetsResource.updateProcessSheetOverheads(this.processSheetId, overheadIds).pipe(
      tap((processSheet) => {
        this.overheadIds = processSheet.overheadIds ?? overheadIds;
      }),
      switchMap(() => firstValueFrom(this.api.resources.overheads.get(overheadId))),
      tap((overhead) => {
        if (!this.linkedOverheads.some((o) => o.id === overhead.id)) {
          this.linkedOverheads = [...this.linkedOverheads, overhead];
        }
      }),
    );
  }

  put(_entity: Partial<Overhead>): Observable<Overhead> {
    throw new Error('put is not supported for process sheet overhead links');
  }

  patch(_id: TId, _values: Record<PropertyKey, unknown>): Observable<unknown> {
    return of(undefined);
  }

  delete(id: TId): Observable<unknown> {
    const overheadIds = this.overheadIds.filter((x) => x !== id);
    return this.processSheetsResource.updateProcessSheetOverheads(this.processSheetId, overheadIds).pipe(
      tap((processSheet) => {
        this.overheadIds = processSheet.overheadIds ?? overheadIds;
        this.linkedOverheads = this.linkedOverheads.filter((o) => o.id !== id);
      }),
      map(() => undefined),
    );
  }
}
